<template>
  <layout-admin current="invoices">
    <div class="panel">
      <div class="panel__head">
        <div>
          <h1>Invoices</h1>
          <span>{{ visibleInvoices.length }} / {{ invoices.length }}</span>
        </div>
        <div class="filters">
          <select :value="filters.client || ''" @change="onClientChange">
            <option value="">All clients</option>
            <option v-for="client in clients" :key="client.id" :value="client.id">
              {{ client.name }}
            </option>
          </select>
          <select :value="filters.status || ''" @change="onStatusChange">
            <option value="">All statuses</option>
            <option v-for="status in statuses" :key="status.value" :value="status.value">
              {{ status.label }}
            </option>
          </select>
          <input
            v-model="query"
            type="search"
            placeholder="Search number, client, email"
          />
        </div>
      </div>

      <div v-if="!visibleInvoices.length" class="panel__empty">No invoices.</div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Number</th>
              <th>Client</th>
              <th>Email</th>
              <th>Issue</th>
              <th>Status</th>
              <th class="num">Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="invoice in visibleInvoices" :key="invoice.id">
              <td class="mono">{{ invoice.number }}</td>
              <td>
                {{ invoice.billed_to_name }}
                <div class="muted">{{ invoice.project_id }}</div>
              </td>
              <td>{{ invoice.billed_to_email }}</td>
              <td>{{ formatDate(invoice.issued_at) }}</td>
              <td><span class="pill" :class="'is-' + invoice.status">{{ invoice.status_label || invoice.status }}</span></td>
              <td class="num">{{ money(invoice.total) }}</td>
              <td class="actions">
                <button type="button" class="btn is-view" @click="openInvoice(invoice)">View</button>
                <button
                  v-if="invoice.has_pdf"
                  type="button"
                  class="btn is-preview"
                  @click="previewInvoice(invoice)"
                >Preview</button>
                <button
                  v-if="invoice.has_receipt"
                  type="button"
                  class="btn is-receipt"
                  @click="previewReceipt(invoice)"
                >Receipt</button>
                <button
                  v-if="invoice.can_mark_paid"
                  type="button"
                  class="btn is-paid"
                  :disabled="acting"
                  @click="markPaid(invoice)"
                >Mark paid</button>
                <button
                  v-if="invoice.can_send"
                  type="button"
                  class="btn is-resend"
                  :disabled="acting"
                  @click="sendInvoice(invoice)"
                >Send</button>
                <button
                  v-if="invoice.can_cancel"
                  type="button"
                  class="btn is-cancel"
                  :disabled="acting"
                  @click="cancelInvoice(invoice)"
                >Cancel</button>
                <button
                  v-if="invoice.can_resend"
                  type="button"
                  class="btn is-resend"
                  :disabled="acting"
                  @click="resendInvoice(invoice)"
                >Resend</button>
                <button
                  v-if="invoice.can_resend_receipt"
                  type="button"
                  class="btn is-resend"
                  :disabled="acting"
                  @click="resendReceipt(invoice)"
                >Resend receipt</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="opened" class="modal" @click.self="closeInvoice">
      <div class="modal__card" role="dialog" aria-modal="true" :aria-label="opened.number">
        <div class="modal__head">
          <strong class="mono">{{ opened.number }}</strong>
          <span class="pill" :class="'is-' + opened.status">{{ opened.status_label || opened.status }}</span>
        </div>
        <div class="sheet">
          <div class="sheet__info">
            <div>
              <div class="sheet__label">Billed To</div>
              <div class="sheet__name">
                {{ opened.billed_to_name }}
                <template v-if="opened.billed_to_registration">
                  <br />({{ opened.billed_to_registration }})
                </template>
              </div>
              <div class="sheet__addr">{{ opened.billed_to_address }}</div>
              <div class="sheet__email">Email: {{ opened.billed_to_email }}</div>
            </div>
            <div>
              <div class="sheet__label">Invoice Information</div>
              <div class="sheet__meta">
                <div><span>Invoice No</span><strong class="mono">{{ opened.number }}</strong></div>
                <div><span>Project ID</span><strong>{{ opened.project_id }}</strong></div>
                <div><span>Issue Date</span><strong>{{ formatLongDate(opened.issued_at) }}</strong></div>
                <div><span>Due Date</span><strong>{{ formatLongDate(opened.due_at) }}</strong></div>
                <div><span>Send Date</span><strong>{{ formatLongDate(opened.send_at) }}</strong></div>
                <div><span>Paid Date</span><strong>{{ formatLongDate(opened.paid_at) }}</strong></div>
                <div v-if="opened.receipt_number"><span>Receipt</span><strong>{{ opened.receipt_number }}</strong></div>
                <div v-if="opened.payment_method"><span>Method</span><strong>{{ opened.payment_method }}</strong></div>
              </div>
            </div>
          </div>

          <table class="sheet-items">
            <thead>
              <tr>
                <th>Item</th>
                <th class="num">Qty</th>
                <th class="num">Rate</th>
                <th class="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!opened.items.length">
                <td colspan="4" class="muted">No line items.</td>
              </tr>
              <tr v-for="item in opened.items" :key="item.id">
                <td>
                  <div class="item-title">{{ item.title }}</div>
                  <div v-if="item.description" class="item-desc">{{ item.description }}</div>
                </td>
                <td class="num">{{ item.quantity }}</td>
                <td class="num">{{ money(item.rate) }}</td>
                <td class="num">{{ money(item.amount) }}</td>
              </tr>
            </tbody>
          </table>

          <table class="sheet-summary">
            <tbody>
              <tr>
                <td></td>
                <td>Sub-Total</td>
                <td>{{ money(sheetSubtotalPositive) }}</td>
              </tr>
              <tr>
                <td></td>
                <td>Discounts</td>
                <td>{{ sheetDiscountTotal < 0 ? money(sheetDiscountTotal) : 'N/A' }}</td>
              </tr>
              <tr>
                <td></td>
                <td>Taxes</td>
                <td>N/A</td>
              </tr>
              <tr class="grand">
                <td></td>
                <td>Total Due</td>
                <td>{{ money(opened.total) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer class="modal__foot">
          <div v-if="opened.can_mark_paid" class="modal__paid">
            <input v-model="paidDate" type="date" class="paid-date" />
            <select v-model="paidMethod" class="paid-date">
              <option v-for="method in paymentMethods" :key="method" :value="method">
                {{ method }}
              </option>
            </select>
          </div>
          <div class="modal__actions">
            <button
              v-if="opened.can_mark_paid"
              type="button"
              class="btn is-paid"
              :disabled="acting"
              @click="markPaid(opened, paidDate, paidMethod)"
            >Mark paid</button>
            <button
              v-if="opened.can_send"
              type="button"
              class="btn is-resend"
              :disabled="acting"
              @click="sendInvoice(opened)"
            >Send</button>
            <button
              v-if="opened.can_cancel"
              type="button"
              class="btn is-cancel"
              :disabled="acting"
              @click="cancelInvoice(opened)"
            >Cancel</button>
            <button
              v-if="opened.can_resend"
              type="button"
              class="btn is-resend"
              :disabled="acting"
              @click="resendInvoice(opened)"
            >Resend</button>
            <button
              v-if="opened.can_resend_receipt"
              type="button"
              class="btn is-resend"
              :disabled="acting"
              @click="resendReceipt(opened)"
            >Resend receipt</button>
            <button
              v-if="opened.has_pdf"
              type="button"
              class="btn is-preview"
              @click="previewInvoice(opened)"
            >Preview</button>
            <button
              v-if="opened.has_receipt"
              type="button"
              class="btn is-receipt"
              @click="previewReceipt(opened)"
            >Receipt</button>
            <button type="button" class="btn is-close" @click="closeInvoice">Close</button>
          </div>
        </footer>
      </div>
    </div>
  </layout-admin>
</template>

<style lang="scss" scoped>
.sheet {
  color: #666;
  flex: 1;
  font-size: 0.85rem;
  min-height: 0;
  overflow: auto;
  padding: 1.15rem 0 1.25rem;
}

.sheet__info {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr 1fr;
  margin: 0 1.1rem 2rem;
}

.sheet__label {
  color: #888;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  margin-bottom: 0.4rem;
  text-transform: uppercase;
}

.sheet__name {
  color: #333;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.25;
}

.sheet__addr,
.sheet__email {
  color: #666;
  font-size: 0.8rem;
  line-height: 1.35;
  margin-top: 0.35rem;
  white-space: pre-line;
}

.sheet__email {
  overflow-wrap: anywhere;
}

.sheet__meta {
  div {
    border-bottom: solid 2px #eee;
    display: flex;
    font-size: 0.8rem;
    justify-content: space-between;
    padding: 0.35rem 0;
  }

  span {
    color: #666;
    font-weight: 600;
  }

  strong {
    color: #333;
    font-weight: 400;
  }
}

.sheet-items {
  border-collapse: collapse;
  margin: 0 1.1rem;
  table-layout: fixed;
  width: calc(100% - 2.2rem);

  th,
  td {
    vertical-align: top;
  }

  th {
    background: #eee;
    color: #666;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    padding: 0.55rem 0.75rem;
    text-transform: uppercase;
  }

  td {
    border-bottom: solid 2px #eee;
    padding: 0.75rem;
  }

  th:nth-child(1),
  td:nth-child(1) {
    width: 55%;
  }

  th:nth-child(2),
  th:nth-child(3),
  th:nth-child(4),
  td:nth-child(2),
  td:nth-child(3),
  td:nth-child(4) {
    width: 15%;
  }

  td.num {
    text-align: center;
  }

  td.num:last-child {
    text-align: right;
  }

  th.num {
    text-align: center;
  }

  th.num:last-child {
    text-align: right;
  }
}

.item-title {
  color: #333;
  font-weight: 600;
}

.item-desc {
  color: #666;
  font-size: 0.8rem;
  font-style: italic;
  line-height: 1.15;
  margin-top: 0.2rem;
  white-space: pre-line;
}

.sheet-summary {
  border-collapse: collapse;
  margin: 0.35rem 1.1rem 0;
  table-layout: fixed;
  width: calc(100% - 2.2rem);

  td {
    padding: 0.35rem 0.75rem;
  }

  td:nth-child(1) {
    width: 55%;
  }

  td:nth-child(2) {
    color: #666;
    font-size: 0.8rem;
    font-weight: 600;
    text-align: right;
    text-transform: uppercase;
    width: 30%;
  }

  td:nth-child(3) {
    text-align: right;
    width: 15%;
  }

  tr:not(.grand) td:nth-child(2),
  tr:not(.grand) td:nth-child(3) {
    border-bottom: solid 2px #eee;
  }

  tr.grand td:nth-child(2),
  tr.grand td:nth-child(3) {
    background: #eee;
    color: #333;
    font-weight: 600;
  }
}

@media (max-width: 720px) {
  .sheet__info {
    grid-template-columns: 1fr;
  }
}
</style>

<script>
import LayoutAdmin from '@/Layouts/Admin.vue';

export default {
  components: {
    LayoutAdmin
  },

  props: {
    invoices: { type: Array, required: true },
    clients: { type: Array, required: true },
    filters: { type: Object, required: true },
    statuses: { type: Array, default: () => [] },
    paymentMethods: { type: Array, default: () => [] }
  },

  data() {
    return {
      query: '',
      opened: null,
      paidDate: '',
      paidMethod: '',
      acting: false
    };
  },

  computed: {
    visibleInvoices() {
      const needle = this.query.trim().toLowerCase();
      if (!needle) return this.invoices;
      return this.invoices.filter((invoice) => {
        const hay = [
          invoice.number,
          invoice.billed_to_name,
          invoice.billed_to_email,
          invoice.project_id
        ].join(' ').toLowerCase();
        return hay.indexOf(needle) !== -1;
      });
    },

    sheetItems() {
      return (this.opened && this.opened.items) ? this.opened.items : [];
    },

    sheetSubtotalPositive() {
      return this.sheetItems.reduce((sum, item) => {
        const amount = Number(item.amount);
        return sum + (amount >= 0 ? amount : 0);
      }, 0);
    },

    sheetDiscountTotal() {
      return this.sheetItems.reduce((sum, item) => {
        const amount = Number(item.amount);
        return sum + (amount < 0 ? amount : 0);
      }, 0);
    }
  },

  created() {
    this.paidMethod = this.defaultPaymentMethod();
    this._onKeydown = (event) => {
      if (event.key === 'Escape') this.closeInvoice();
    };
    window.addEventListener('keydown', this._onKeydown);
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this._onKeydown);
  },

  methods: {
    defaultPaymentMethod() {
      return (this.paymentMethods && this.paymentMethods[0]) || '';
    },

    openInvoice(invoice) {
      this.opened = invoice;
      this.paidDate = this.todayDate();
      this.paidMethod = this.defaultPaymentMethod();
    },

    closeInvoice() {
      this.opened = null;
    },

    todayDate() {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return now.getFullYear() + '-' + month + '-' + day;
    },

    markPaid(invoice, paidAt, paymentMethod) {
      if (!invoice || !invoice.can_mark_paid || this.acting) {
        return;
      }

      const date = paidAt || this.todayDate();
      const method = paymentMethod || this.paidMethod || this.defaultPaymentMethod();
      if (!window.confirm(
        'Mark ' + invoice.number + ' as paid on ' + this.formatDate(date)
          + ' via ' + method + '? A receipt will be emailed to ' + invoice.billed_to_email + '.'
      )) {
        return;
      }

      this.postInvoice(invoice, this.route('admin.invoices.paid', invoice.id), {
        paid_at: date,
        payment_method: method
      });
    },

    sendInvoice(invoice) {
      if (!invoice || !invoice.can_send || this.acting) {
        return;
      }

      if (!window.confirm(
        'Send invoice ' + invoice.number + ' to ' + invoice.billed_to_email + '?'
      )) {
        return;
      }

      this.postInvoice(invoice, this.route('admin.invoices.send', invoice.id));
    },

    cancelInvoice(invoice) {
      if (!invoice || !invoice.can_cancel || this.acting) {
        return;
      }

      if (!window.confirm(
        'Cancel ' + invoice.number + '? It will not be emailed.'
      )) {
        return;
      }

      this.postInvoice(invoice, this.route('admin.invoices.cancel', invoice.id));
    },

    resendInvoice(invoice) {
      if (!invoice || !invoice.can_resend || this.acting) {
        return;
      }

      if (!window.confirm(
        'Resend invoice ' + invoice.number + ' to ' + invoice.billed_to_email + '?'
      )) {
        return;
      }

      this.postInvoice(invoice, this.route('admin.invoices.resend', invoice.id));
    },

    resendReceipt(invoice) {
      if (!invoice || !invoice.can_resend_receipt || this.acting) {
        return;
      }

      const number = invoice.receipt_number || 'receipt';
      if (!window.confirm(
        'Resend ' + number + ' to ' + invoice.billed_to_email + '?'
      )) {
        return;
      }

      this.postInvoice(invoice, this.route('admin.invoices.resend-receipt', invoice.id));
    },

    postInvoice(invoice, url, data) {
      this.acting = true;
      this.$inertia.post(url, data || {}, {
        preserveScroll: true,
        preserveState: true,
        onFinish: () => {
          this.acting = false;
        },
        onSuccess: () => {
          const next = this.invoices.find((row) => row.id === invoice.id);
          if (this.opened && this.opened.id === invoice.id) {
            this.opened = next || null;
          }
        }
      });
    },

    previewInvoice(invoice) {
      if (!invoice) {
        return;
      }

      window.open(
        this.route('admin.invoices.pdf', invoice.id),
        'invoice-pdf-' + invoice.id,
        'noopener,noreferrer,width=980,height=900,scrollbars=yes,resizable=yes'
      );
    },

    previewReceipt(invoice) {
      if (!invoice || !invoice.has_receipt) {
        return;
      }

      window.open(
        this.route('admin.invoices.receipt', invoice.id),
        'invoice-receipt-' + invoice.id,
        'noopener,noreferrer,width=980,height=900,scrollbars=yes,resizable=yes'
      );
    },

    onClientChange(event) {
      this.applyFilters({ client: event.target.value || '' });
    },

    onStatusChange(event) {
      this.applyFilters({ status: event.target.value || '' });
    },

    applyFilters(next) {
      const client = Object.prototype.hasOwnProperty.call(next, 'client')
        ? next.client
        : (this.filters.client || '');
      const status = Object.prototype.hasOwnProperty.call(next, 'status')
        ? next.status
        : (this.filters.status || '');
      const query = {};
      if (client) query.client = client;
      if (status) query.status = status;
      this.closeInvoice();
      this.$inertia.get(this.route('admin.invoices.index'), query, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      });
    },

    money(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return '—';
      const formatted = Math.abs(num).toLocaleString('en-MY', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return (num < 0 ? '- ' : '') + 'RM ' + formatted;
    },

    formatDate(value) {
      if (!value) return '—';
      const date = new Date(value + (String(value).indexOf('T') === -1 ? 'T00:00:00' : ''));
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    },

    formatLongDate(value) {
      if (!value) return '—';
      const date = new Date(value + (String(value).indexOf('T') === -1 ? 'T00:00:00' : ''));
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  }
};
</script>
